/**
 * CandidatesV2 — All Candidates v2 (UI-v2 Phase 1b-rem).
 *
 * Ports the v1 Candidates.tsx data hooks into the v2 visual shell per
 * design/v2-direction/index.html lines 763-838 (#view-candidates).
 *
 * Layout:
 *   1. Page head — eyebrow + title + sub + Export + Add buttons.
 *   2. JD chips row — one chip per active JD, click updates ?jd= (kept as-is).
 *   3. Toolbar row — search input + compact Filters (status+sort) menu +
 *      inline fit-key swatch (hover) + view toggle.
 *   4. TABLE (default) or CARD view; choice persists per workspace.
 *
 * Table columns (founder-approved): # · Candidate · Key skills · Fit ·
 * Assessment (fit-band + tenure) · Status ▾ (inline stage-advance) · ⋯
 * Rows are PAGINATED 8 per page (founder spec): page 1 = the top 8 (already
 * rank-ordered), the rest paginate. Prev/Next + "Page X of N" — shared by both
 * the Table and Card views, and reset to page 1 on any JD/filter/sort change.
 *
 * ── Density ceiling (sentinels grep source incl. comments) ───────────────────
 * No p-[56789], gap-[56789], space-y-[56789] anywhere in this file.
 * No px-[89], py-[89] anywhere in this file.
 *
 * ── Token discipline ─────────────────────────────────────────────────────────
 * No raw hex literals. No hsl(var(--chart-N)) — chart tokens are raw HEX;
 * only the fit-band set (--strong / --moderate / --weak / --brand / --brand-*
 * / --strong-soft / --moderate-soft / --weak-soft) and shadcn-standard HSL
 * triples (--foreground, --muted-foreground, --card, --muted, --input, --background)
 * are safe inside hsl(). Pattern follows DashboardV2 exactly.
 */
import { useEffect, useRef } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useTenantBase } from "@/hooks/useTenantBase";
import { Plus, Download, Search, SlidersHorizontal, MoreHorizontal, Check, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { useJds } from "@/hooks/queries/candidates/useJds";
import { useCandidates } from "@/hooks/queries/candidates/useCandidates";
import { useRanking } from "@/hooks/queries/candidates/useRanking";
import { useRankingRunByResource } from "@/hooks/queries/candidates/useRankingRunByResource";
import { RankingProgressBanner } from "@/components/candidates/table/RankingProgressBanner";
import { CandidatePhoto } from "@/components/candidates/CandidatePhoto";
import { DataTableV2 } from "@/components/v2/patterns/DataTableV2";
import type { DataTableColumn } from "@/components/v2/patterns/DataTableV2";
import { useCandidateTableRows } from "@/pages/app/Candidates/useCandidateTableRows";
import { useTableFilters } from "@/pages/app/Candidates/useTableFilters";
import type { CandidateTableRow, SortKey } from "@/pages/app/Candidates/types";
import { TERMINAL_STATUSES } from "@/pages/app/Candidates/types";
import type { CandidateStatus } from "@/components/candidates/StatusPill";
import { CandidateCard } from "@/components/candidate/CandidateCard";
import { CandidateStatusSelect } from "@/components/candidate/CandidateStatusSelect";
import { Skeleton } from "@/components/ui/skeleton";
import {
  CandidatesTableSkeleton,
  CandidatesCardsSkeleton,
} from "@/components/v2/candidates/CandidatesLoadingState";

// ── Fit-band thresholds (canonical: strong ≥85, moderate 65-84, weak <65) ──
function fitBand(score: number | null | undefined): "strong" | "moderate" | "weak" {
  if (score == null) return "weak";
  if (score >= 85) return "strong";
  if (score >= 65) return "moderate";
  return "weak";
}

// ── Status label map ─────────────────────────────────────────────────────────
const STATUS_LABELS: Record<CandidateStatus, string> = {
  active: "Active",
  phone_screen: "Phone screen",
  interview_scheduled: "Interview scheduled",
  interviewed: "Interviewed",
  offer: "Offer",
  hired: "Hired",
  rejected: "Rejected",
  opted_out: "Opted out",
};

// ── Ordered status options for the filter dropdown (matches V1's 8-value set) ──
const STATUS_OPTIONS: CandidateStatus[] = [
  "active",
  "phone_screen",
  "interview_scheduled",
  "interviewed",
  "offer",
  "hired",
  "rejected",
  "opted_out",
];

// ── Sort options for the toolbar sort dropdown (axis only; dir is the toggle) ──
const SORT_OPTIONS: { key: SortKey; label: string }[] = [
  { key: "rank", label: "Rank" },
  { key: "fit", label: "Fit" },
  { key: "name", label: "Name" },
  { key: "status", label: "Status" },
  { key: "tenure", label: "Tenure" },
];

// ── Tenure label + band mapping (Assessment column) ──────────────────────────
// tenure_signal is strong | moderate | concern; "concern" borrows the weak
// (red) fit palette, matching CandidateCard's tenure chip.
const TENURE_LABEL: Record<"strong" | "moderate" | "concern", string> = {
  strong: "Strong tenure",
  moderate: "Moderate tenure",
  concern: "Concern",
};

// ── Rich empty state (L8) — token-styled v2 card with variant copy + CTA. ────
function CandidatesEmptyState({
  variant,
  tenantSlug,
  jdId,
  onClearFilters,
}: {
  variant: "no-jds" | "no-candidates" | "no-matches";
  tenantSlug: string | undefined;
  jdId: string | undefined;
  onClearFilters: () => void;
}): JSX.Element {
  const base = useTenantBase();
  const copy = {
    "no-jds": {
      title: "No roles yet",
      body: "Add a job description and CertAIn will rank candidates against it.",
      cta: tenantSlug
        ? { to: `${base}/jobs/new`, label: "Create a job" }
        : null,
    },
    "no-candidates": {
      title: "No candidates yet",
      body: "Upload candidate resumes and CertAIn will rank them against this role.",
      cta: tenantSlug
        ? {
            to: `${base}/candidates/new${jdId ? `?jd=${jdId}` : ""}`,
            label: "Add candidates",
          }
        : null,
    },
    "no-matches": {
      title: "No candidates match your filters",
      body: "Try a different status or clear the search to see everyone in this role.",
      cta: null,
    },
  }[variant];

  return (
    <div
      className="flex flex-col items-center justify-center rounded-[var(--radius)] border border-border-default bg-surface-card px-4 text-center shadow-[var(--shadow-sm)]"
      style={{ paddingTop: "48px", paddingBottom: "48px" }}
      data-testid="v2-candidates-empty"
    >
      <h3 className="text-[15px] font-semibold text-text-primary">{copy.title}</h3>
      <p className="mt-1 max-w-sm text-[13px] text-text-secondary">{copy.body}</p>
      {variant === "no-matches" ? (
        <Button variant="outline" size="sm" className="mt-4" onClick={onClearFilters}>
          Clear filters
        </Button>
      ) : copy.cta ? (
        <Button asChild size="sm" className="mt-4">
          <Link to={copy.cta.to}>
            <Plus className="h-3.5 w-3.5" aria-hidden />
            {copy.cta.label}
          </Link>
        </Button>
      ) : null}
    </div>
  );
}

// ── Table column definitions (matches spec column order). Sortable columns opt
//    in via `sortField` (drives the DataTableV2 header sort chrome). ──
const COLUMNS: DataTableColumn[] = [
  { key: "rank", header: "#", width: "48px", headClassName: "text-center", cellClassName: "text-center", sortField: "rank" },
  { key: "candidate", header: "Candidate", sortField: "name" },
  { key: "skills", header: "Key skills", width: "220px" },
  { key: "fit_score", header: "Fit", width: "150px", sortField: "fit" },
  { key: "assessment", header: "Assessment", width: "210px", sortField: "tenure" },
  { key: "status", header: "Status", width: "168px", sortField: "status" },
  { key: "actions", header: "", width: "52px" },
];

export default function CandidatesV2(): JSX.Element {
  const { tenantSlug } = useParams<{ tenantSlug: string }>();
  const base = useTenantBase();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const jdId = searchParams.get("jd") ?? undefined;
  // View toggle — founder-approved DEFAULT is now Table (Cards is the toggle).
  // Precedence: explicit ?view= (shareable, wins) → per-workspace localStorage
  // preference → "table" default. Toggling writes BOTH the URL (shareable +
  // remount-stable) and the per-tenant localStorage key, so the user's choice
  // sticks across visits within a workspace without leaking across tenants.
  const viewStorageKey = tenantSlug
    ? `certain.v2.candidatesView.${tenantSlug}`
    : null;
  const readStoredView = (): "table" | "cards" | null => {
    if (!viewStorageKey || typeof window === "undefined") return null;
    const v = window.localStorage.getItem(viewStorageKey);
    return v === "table" || v === "cards" ? v : null;
  };
  const urlView = searchParams.get("view");
  const viewMode: "table" | "cards" =
    urlView === "table" || urlView === "cards"
      ? urlView
      : readStoredView() ?? "table";
  const setViewMode = (mode: "table" | "cards"): void => {
    if (viewStorageKey && typeof window !== "undefined") {
      window.localStorage.setItem(viewStorageKey, mode);
    }
    const next = new URLSearchParams(searchParams);
    next.set("view", mode);
    setSearchParams(next);
  };

  // ── Data hooks ──────────────────────────────────────────────────────────────
  const jdsQ = useJds({ statusFilter: "active" });
  // perf/candidates-load-latency (#3): resolve the DEFAULT jd inline from the
  // just-loaded list so the JD-scoped queries below fire the moment `useJds`
  // settles — WITHOUT waiting for the `?jd=` URL-writeback rerender (the effect
  // that runs a React commit cycle later). When `?jd=` is already in the URL,
  // `jdId` is set on the first render and the queries fire immediately as
  // before (no dependency on `useJds`). This removes the extra rerender stall
  // from the bare `/candidates` (no-jd) landing without changing the shareable
  // URL behavior — the writeback effect below still runs for chip highlight +
  // link sharing, and resolves to this same id (so no second fetch).
  const defaultJdId =
    !jdId && jdsQ.isSuccess && jdsQ.data.length > 0
      ? [...jdsQ.data].sort(
          (a, b) => +new Date(b.created_at) - +new Date(a.created_at),
        )[0]?.id
      : undefined;
  const effectiveJdId = jdId ?? defaultJdId;
  // ROOT FIX (v2-candidates-ranked-data): scope the candidate fetch to the
  // SELECTED JD — `useCandidates({ jdId })` forwards `jd_id=` so the backend
  // returns the JD's *paired* candidates (project_jd_candidate_model.md Option
  // C), exactly like the V1 Dashboard (Dashboard.tsx:159). The prior
  // `useCandidates()` (no jdId) returned the tenant-wide list capped at the
  // backend `_DEFAULT_LIMIT = 25` (candidates.py:80) — an alphabetical first
  // page that did NOT contain the JD's ranked heroes (Marcus Caputo et al.),
  // so the `useRanking({ jdId })` join below found nothing to decorate and
  // every row rendered rank/fit/skills/why as "—". Scoping to the JD aligns
  // the candidate population with the ranking rows, so the join lands.
  const candidatesQ = useCandidates({ jdId: effectiveJdId });
  const rankingQ = useRanking({ jdId: effectiveJdId });
  // L4: in-flight ranking indicator — the latest run for the selected JD.
  const runQ = useRankingRunByResource({ jdId: effectiveJdId });
  const inFlight =
    runQ.data?.status === "queued" || runQ.data?.status === "running";

  const filters = useTableFilters();

  // ── Default-JD writeback (copied exactly from v1 Candidates.tsx) ─────────
  useEffect(() => {
    if (jdId) return;
    if (!jdsQ.isSuccess) return;
    const jds = jdsQ.data;
    if (jds.length === 0) return;
    const sorted = [...jds].sort(
      (a, b) => +new Date(b.created_at) - +new Date(a.created_at),
    );
    const firstJd = sorted[0];
    if (!firstJd) return;
    const next = new URLSearchParams(searchParams);
    next.set("jd", firstJd.id);
    setSearchParams(next, { replace: true });
  }, [jdId, jdsQ.isSuccess, jdsQ.data, searchParams, setSearchParams]);

  // ── Table rows (join + filter + sort) ────────────────────────────────────
  const rows = useCandidateTableRows({
    candidates: candidatesQ.data,
    rankings: rankingQ.data,
    filterStatus: filters.status,
    filterQ: filters.q,
    sortKey: filters.sortKey,
    sortDir: filters.sortDir,
  });

  const totalCandidates = candidatesQ.data?.length ?? 0;
  const jds = jdsQ.data ?? [];

  // ── Empty-state selection (L8) — order: no-jds → no-candidates → no-matches. ─
  let emptyVariant: "no-jds" | "no-candidates" | "no-matches" | null = null;
  if (jdsQ.isSuccess && jds.length === 0) {
    emptyVariant = "no-jds";
  } else if (candidatesQ.isSuccess && totalCandidates === 0) {
    emptyVariant = "no-candidates";
  } else if (candidatesQ.isSuccess && rows.length === 0) {
    emptyVariant = "no-matches";
  }

  // ── Loading gate (founder bug: NEVER flash "0 candidates") ─────────────────
  // The false-empty window is any time we don't yet have SETTLED data for the
  // CURRENTLY-selected JD: (a) the JD list is still loading, (b) the default
  // ?jd= is still being written back (chip not yet chosen), (c) the candidates
  // query is pending — including the fresh query key minted on a JD-switch, so
  // switching role chips shows a skeleton, not a flash of 0 — or (d) the ranking
  // query is pending. We wait on the ranking too: the default sort is "rank", so
  // painting undecorated rows first would reflow/reorder when ranking lands.
  //
  // `no-jds` is deliberately NOT loading: with zero roles, ?jd= never resolves
  // so `rankingQ` stays disabled-pending forever — the rich no-jds empty state
  // must win. It is evaluated BEFORE this gate in the render tree below.
  //
  // Uses `isPending` (status === "pending" ⇒ no data yet), NOT `isFetching`, so
  // a cached revisit within gcTime paints instantly and a background refetch
  // (staleTime elapsed, data still present) never blanks the screen.
  const noJds = jdsQ.isSuccess && jds.length === 0;
  // perf/candidates-load-latency (#3): gate on `effectiveJdId`, not the URL
  // `jdId`. Once the default jd is resolved inline the JD-scoped queries are
  // already in flight, so the skeleton clears as soon as THEY settle — we no
  // longer hold the skeleton for the extra `?jd=` URL-writeback rerender.
  const awaitingJdDefault = jdsQ.isSuccess && jds.length > 0 && !effectiveJdId;
  const isDataPending =
    !noJds &&
    (!jdsQ.isSuccess ||
      awaitingJdDefault ||
      candidatesQ.isPending ||
      rankingQ.isPending);

  // ── Pagination (8 per page; page 1 = top-8 by fit rank) ──────────────────
  // `rows` is already sorted by fit (default sort) + filtered; pagination sits
  // ON TOP of that. URL-backed (?page=N) — same persistence model as ?view / ?jd
  // / the filters — so a page is shareable and survives remounts. Applies to
  // BOTH the Table and Card views (each shows the same 8-row window).
  const PAGE_SIZE = 8;
  const pageCount = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const rawPage = Number.parseInt(searchParams.get("page") ?? "1", 10);
  // Clamp into [1, pageCount] so an out-of-range ?page= (e.g. after a filter
  // shrinks the list) can never render an empty window.
  const currentPage = Number.isFinite(rawPage)
    ? Math.min(Math.max(rawPage, 1), pageCount)
    : 1;
  const pageRows = rows.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const goToPage = (p: number): void => {
    const clamped = Math.min(Math.max(p, 1), pageCount);
    const next = new URLSearchParams(searchParams);
    if (clamped <= 1) next.delete("page");
    else next.set("page", String(clamped));
    setSearchParams(next);
  };

  // Reset to page 1 whenever the JD / filters / sort change — otherwise a page-3
  // position would silently carry over onto a different (or reordered) list.
  // Guarded by a signature ref so a shared ?page= link still lands on load
  // (no reset on the first render) and the effect never loops.
  const filterSig = `${jdId ?? ""}|${filters.status ?? ""}|${filters.q}|${filters.sortKey}|${filters.sortDir}`;
  const prevFilterSig = useRef(filterSig);
  useEffect(() => {
    if (prevFilterSig.current === filterSig) return;
    prevFilterSig.current = filterSig;
    if (searchParams.has("page")) {
      const next = new URLSearchParams(searchParams);
      next.delete("page");
      setSearchParams(next, { replace: true });
    }
  }, [filterSig, searchParams, setSearchParams]);

  // ── JD chip click ─────────────────────────────────────────────────────────
  const onJdChipClick = (id: string): void => {
    const next = new URLSearchParams(searchParams);
    next.set("jd", id);
    setSearchParams(next);
  };

  // ── Row click → candidate detail ─────────────────────────────────────────
  const onRowClick = (row: CandidateTableRow): void => {
    const dest = `${base}/candidates/${row.id}${jdId ? `?jd=${jdId}` : ""}`;
    navigate(dest);
  };

  // ── Clear active filters (search + status) ────────────────────────────────
  const onClearFilters = (): void => {
    filters.setStatus(null);
    filters.setQ("");
  };

  // ── L5: real client-side CSV export of the currently filtered/sorted rows. ─
  // Honest, functional export (no stub) — exports exactly what the user sees.
  const onExportCsv = (): void => {
    const header = [
      "Rank",
      "Candidate",
      "Fit score",
      "Assessment",
      "Status",
      "Last activity",
    ];
    const escape = (v: string): string =>
      /[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
    const lines = rows.map((r) =>
      [
        r.rank_position != null ? String(r.rank_position) : "",
        r.name,
        r.fit_score != null ? String(r.fit_score) : "",
        r.fit_overall ?? "",
        STATUS_LABELS[r.status] ?? r.status,
        r.last_activity_at ?? "",
      ]
        .map((c) => escape(String(c)))
        .join(","),
    );
    const csv = [header.join(","), ...lines].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const roleSlug = jds.find((j) => j.id === jdId)?.name ?? "candidates";
    a.download = `${roleSlug.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}-candidates.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // ── Terminal rows (opted_out/rejected) get opacity-60 ────────────────────
  const getRowClassName = (row: CandidateTableRow): string | undefined => {
    return TERMINAL_STATUSES.has(row.status) ? "opacity-60" : undefined;
  };

  // ── Cell renderer ─────────────────────────────────────────────────────────
  const renderCell = (row: CandidateTableRow, col: DataTableColumn, i: number): React.ReactNode => {
    const rank = row.rank_position;
    const score = row.fit_score;
    const band = fitBand(score);

    // L12: rank badge is brand-tinted ONLY for the top 5; lower ranks render a
    // plain neutral badge (matches CandidateCard.rankIsTop + the mockup).
    const rankIsTop = rank != null && rank <= 5;

    switch (col.key) {
      case "rank":
        return rank != null ? (
          <span
            className="inline-grid place-items-center tabular-nums"
            style={{
              width: "24px",
              height: "24px",
              borderRadius: "6px",
              fontSize: "12px",
              fontWeight: 650,
              background: rankIsTop
                ? "hsl(var(--brand,214_100%_41%) / 0.12)"
                : "var(--surface-subtle)",
              color: rankIsTop
                ? "hsl(var(--brand,214_100%_41%))"
                : "var(--text-primary)",
            }}
          >
            {rank}
          </span>
        ) : (
          <span className="text-text-tertiary text-[12px]">{i + 1}</span>
        );

      case "candidate":
        // Hover reveals the AI "why" snippet (native title) — the table stays
        // lean (no full blurb in a cell) while the reasoning is one hover away.
        return (
          <div className="flex items-center gap-[10px]">
            <CandidatePhoto candidateId={row.id} candidateName={row.name} size="sm" />
            <span
              className="truncate text-text-primary"
              style={{ fontWeight: 600 }}
              title={row.summary ?? undefined}
            >
              {row.name}
            </span>
          </div>
        );

      case "skills":
        // Approved replacement for the low-signal "Current role" column: the
        // top 2-3 JD-anchored, evidence-gated skill tags (rankings.skills_tags).
        // Honest em-dash when the row has no ranking yet (empty tags).
        return row.skills_tags.length > 0 ? (
          <div className="flex flex-wrap items-center" style={{ gap: "5px" }}>
            {row.skills_tags.slice(0, 3).map((s) => (
              <span
                key={s}
                className="inline-flex items-center"
                style={{
                  borderRadius: "999px",
                  background: "hsl(var(--brand-soft))",
                  color: "hsl(var(--brand,214_100%_41%))",
                  border: "1px solid hsl(var(--brand,214_100%_41%) / 0.22)",
                  padding: "2px 8px",
                  fontSize: "10.5px",
                  fontWeight: 550,
                }}
              >
                {s}
              </span>
            ))}
          </div>
        ) : (
          <span className="text-text-tertiary text-[13px]">—</span>
        );

      case "fit_score":
        return score != null ? (
          <div className="flex items-center gap-[10px]">
            <div
              className="flex-1 overflow-hidden rounded-full"
              style={{
                height: "6px",
                background: "var(--surface-subtle)",
                minWidth: "80px",
              }}
            >
              <span
                className="block h-full rounded-full"
                style={{
                  width: `${score}%`,
                  background:
                    band === "strong"
                      ? "hsl(var(--strong,165_88%_30%))"
                      : band === "moderate"
                        ? "hsl(var(--moderate,36_88%_40%))"
                        : "hsl(var(--weak,0_72%_46%))",
                }}
              />
            </div>
            <span
              className="tabular-nums text-text-primary shrink-0"
              style={{ fontSize: "13.5px", fontWeight: 650, width: "26px", textAlign: "right" }}
            >
              {score}
            </span>
          </div>
        ) : (
          <span className="text-text-tertiary text-[13px]">—</span>
        );

      case "assessment": {
        // Fit band + tenure together (founder-approved: e.g. "Strong fit ·
        // Strong tenure"). Two band-colored soft pills; each hides when its
        // source is null. Fully unranked rows show an honest em-dash.
        const tenureBand: "strong" | "moderate" | "weak" | null =
          row.tenure_signal == null
            ? null
            : row.tenure_signal === "strong"
              ? "strong"
              : row.tenure_signal === "moderate"
                ? "moderate"
                : "weak"; // concern → weak palette
        if (row.fit_overall == null && row.tenure_signal == null) {
          return <span className="text-text-tertiary text-[12px]">—</span>;
        }
        const softBg = (b: "strong" | "moderate" | "weak"): string =>
          b === "strong"
            ? "hsl(var(--strong-soft))"
            : b === "moderate"
              ? "hsl(var(--moderate-soft))"
              : "hsl(var(--weak-soft))";
        const fg = (b: "strong" | "moderate" | "weak"): string =>
          b === "strong"
            ? "hsl(var(--strong))"
            : b === "moderate"
              ? "hsl(var(--moderate))"
              : "hsl(var(--weak))";
        return (
          <div className="flex flex-wrap items-center" style={{ gap: "5px" }}>
            {row.fit_overall != null ? (
              <span
                className="inline-flex items-center gap-[4px]"
                style={{
                  borderRadius: "999px",
                  padding: "2px 8px",
                  fontSize: "10.5px",
                  fontWeight: 600,
                  background: softBg(row.fit_overall),
                  color: fg(row.fit_overall),
                }}
              >
                <span
                  className="inline-block rounded-full shrink-0"
                  style={{ width: "6px", height: "6px", background: fg(row.fit_overall) }}
                />
                {row.fit_overall.charAt(0).toUpperCase() + row.fit_overall.slice(1)} fit
              </span>
            ) : null}
            {tenureBand != null ? (
              <span
                className="inline-flex items-center"
                style={{
                  borderRadius: "999px",
                  padding: "2px 8px",
                  fontSize: "10.5px",
                  fontWeight: 600,
                  background: softBg(tenureBand),
                  color: fg(tenureBand),
                }}
              >
                {TENURE_LABEL[row.tenure_signal!]}
              </span>
            ) : null}
          </div>
        );
      }

      case "status":
        // Inline stage-advance — the Status column is an editable dropdown that
        // changes pipeline stage in place (V1 parity via useUpdateCandidateStatus).
        return (
          <CandidateStatusSelect candidateId={row.id} currentStatus={row.status} />
        );

      case "actions":
        // ⋯ overflow menu keeps secondary actions off the lean row.
        return (
          <div
            onClick={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
            className="flex justify-center"
          >
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={`Actions for ${row.name}`}
                >
                  <MoreHorizontal className="h-4 w-4" aria-hidden />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onSelect={() => onRowClick(row)}>
                  View profile
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div>
      {/* ── PAGE HEAD ────────────────────────────────────────────────────── */}
      <div
        className="flex items-center gap-[14px]"
        style={{ marginBottom: "22px" }}
      >
        <div className="min-w-0 flex-1">
          <div
            className="text-[hsl(var(--brand,214_100%_41%))] font-bold uppercase"
            style={{ fontSize: "11px", letterSpacing: "0.08em" }}
          >
            Pipeline
          </div>
          <h1
            className="text-text-primary"
            style={{ fontSize: "22px", fontWeight: 650, letterSpacing: "-0.02em", margin: 0 }}
          >
            All Candidates
          </h1>
          <div
            className="text-text-secondary"
            style={{ fontSize: "13.5px", marginTop: "3px" }}
          >
            {isDataPending ? (
              // Loading treatment for the count — NEVER "0 candidates" while the
              // candidates/ranking queries are pending (founder bug fix).
              <Skeleton
                className="h-[14px] w-[280px]"
                data-testid="v2-candidates-subtitle-loading"
              />
            ) : (
              <>
                {totalCandidates} candidate{totalCandidates !== 1 ? "s" : ""} · ranked by AI fit for the selected role
              </>
            )}
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={onExportCsv}
          disabled={rows.length === 0}
          title="Download the current list as CSV"
        >
          <Download className="h-3.5 w-3.5" aria-hidden />
          Export
        </Button>
        <Button
          size="sm"
          onClick={() =>
            navigate(
              `${base}/candidates/new${jdId ? `?jd=${jdId}` : ""}`,
            )
          }
        >
          <Plus className="h-3.5 w-3.5" aria-hidden />
          Add candidate
        </Button>
      </div>

      {/* ── JD CHIPS ROW ─────────────────────────────────────────────────── */}
      {jds.length > 0 && (
        <div
          className="flex flex-wrap items-center gap-[8px]"
          style={{ marginBottom: "16px" }}
        >
          {jds.map((jd) => {
            const isActive = jd.id === jdId;
            // L9: per-JD candidate count badge (mockup `.chip .c`). The JD LIST
            // endpoint returns `candidate_count`; omit the badge when absent.
            const count = jd.candidate_count;
            return (
              <button
                key={jd.id}
                type="button"
                onClick={() => onJdChipClick(jd.id)}
                className={cn(
                  "inline-flex items-center gap-[8px] rounded-full border transition-colors cursor-pointer",
                  isActive
                    ? "border-[hsl(var(--brand,214_100%_41%))] bg-surface-subtle text-text-primary font-semibold"
                    : "border-border-default bg-surface-canvas text-text-secondary font-medium hover:bg-surface-subtle hover:text-text-primary",
                )}
                style={{ height: "32px", padding: "0 13px", fontSize: "13px" }}
              >
                {jd.name}
                {count != null ? (
                  <span
                    className={cn(
                      "inline-flex items-center justify-center rounded-full tabular-nums",
                      isActive ? "text-text-primary" : "text-text-secondary",
                    )}
                    style={{
                      minWidth: "18px",
                      height: "18px",
                      padding: "0 5px",
                      fontSize: "11px",
                      fontWeight: 600,
                      background: "var(--surface-card)",
                      border: "1px solid var(--border-default)",
                    }}
                  >
                    {count}
                  </span>
                ) : null}
              </button>
            );
          })}
        </div>
      )}

      {/* ── TOOLBAR ROW ──────────────────────────────────────────────────── */}
      <div
        className="flex items-center flex-wrap gap-[12px]"
        style={{ marginBottom: "16px" }}
      >
        {/* Search input */}
        <div className="relative" style={{ width: "260px" }}>
          <Search
            className="absolute left-[10px] top-1/2 -translate-y-1/2 text-text-tertiary"
            style={{ width: "15px", height: "15px" }}
            aria-hidden
          />
          <Input
            className="pl-[34px]"
            placeholder="Filter candidates…"
            value={filters.q}
            onChange={(e) => filters.setQ(e.target.value)}
          />
        </div>
        {/* Compact Filters control — folds Status + Sort (axis + direction)
            into one menu to tidy the band. Items keep the menu open (prevent
            default) so several picks can be made in a row; a count badge on
            the trigger flags an active status filter. Drives the same URL-backed
            filters as before (applies to BOTH the card and table views). */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              aria-label="Filters and sort"
            >
              <SlidersHorizontal className="h-3.5 w-3.5" aria-hidden />
              Filters
              {filters.status ? (
                <span
                  className="inline-flex items-center justify-center rounded-full tabular-nums"
                  style={{
                    minWidth: "16px",
                    height: "16px",
                    padding: "0 4px",
                    fontSize: "10px",
                    fontWeight: 700,
                    background: "hsl(var(--brand,214_100%_41%) / 0.12)",
                    color: "hsl(var(--brand,214_100%_41%))",
                  }}
                >
                  1
                </span>
              ) : null}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-56">
            <DropdownMenuLabel>Status</DropdownMenuLabel>
            <DropdownMenuItem
              onSelect={(e) => {
                e.preventDefault();
                filters.setStatus(null);
              }}
            >
              <span className="inline-flex w-4 justify-center shrink-0">
                {filters.status == null ? <Check className="h-3.5 w-3.5" aria-hidden /> : null}
              </span>
              All statuses
            </DropdownMenuItem>
            {STATUS_OPTIONS.map((s) => (
              <DropdownMenuItem
                key={s}
                onSelect={(e) => {
                  e.preventDefault();
                  filters.setStatus(s);
                }}
              >
                <span className="inline-flex w-4 justify-center shrink-0">
                  {filters.status === s ? <Check className="h-3.5 w-3.5" aria-hidden /> : null}
                </span>
                {STATUS_LABELS[s]}
              </DropdownMenuItem>
            ))}
            <DropdownMenuSeparator />
            <DropdownMenuLabel>Sort by</DropdownMenuLabel>
            {SORT_OPTIONS.map((o) => (
              <DropdownMenuItem
                key={o.key}
                onSelect={(e) => {
                  e.preventDefault();
                  filters.setSort(o.key, filters.sortDir);
                }}
              >
                <span className="inline-flex w-4 justify-center shrink-0">
                  {filters.sortKey === o.key ? <Check className="h-3.5 w-3.5" aria-hidden /> : null}
                </span>
                {o.label}
              </DropdownMenuItem>
            ))}
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onSelect={(e) => {
                e.preventDefault();
                filters.setSort(
                  filters.sortKey,
                  filters.sortDir === "asc" ? "desc" : "asc",
                );
              }}
            >
              <span className="inline-flex w-4 justify-center shrink-0" aria-hidden>
                {filters.sortDir === "asc" ? "↑" : "↓"}
              </span>
              {filters.sortDir === "asc" ? "Ascending" : "Descending"}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        <div className="flex-1" />

        {/* Fit key — demoted from the full 3-dot legend to a compact inline
            swatch; the numeric thresholds live in the hover tooltip. */}
        <TooltipProvider delayDuration={150}>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                className="inline-flex items-center gap-[6px] text-text-secondary"
                style={{ fontSize: "12px" }}
                aria-label="Fit score key"
              >
                <span className="inline-flex items-center gap-[3px]">
                  {[
                    "hsl(var(--strong,165_88%_30%))",
                    "hsl(var(--moderate,36_88%_40%))",
                    "hsl(var(--weak,0_72%_46%))",
                  ].map((c) => (
                    <span
                      key={c}
                      className="inline-block rounded-full shrink-0"
                      style={{ width: "8px", height: "8px", background: c }}
                    />
                  ))}
                </span>
                Fit key
              </button>
            </TooltipTrigger>
            <TooltipContent>
              Strong 85+ · Moderate 65–84 · Weak &lt;65
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>

        {/* View toggle pill group */}
        <div
          className="inline-flex gap-0.5 rounded-[var(--radius,0.5rem)] bg-surface-subtle"
          style={{ padding: "3px" }}
        >
          {(["cards", "table"] as const).map((mode) => (
            <button
              key={mode}
              type="button"
              onClick={() => setViewMode(mode)}
              className={cn(
                "inline-flex items-center gap-1.5 h-7 transition-colors",
                "rounded-[calc(var(--radius,0.5rem)-3px)]",
                viewMode === mode
                  ? "bg-surface-card text-text-primary shadow-sm"
                  : "text-text-secondary hover:text-text-primary",
              )}
              style={{ padding: "0 11px", fontSize: "12.5px", fontWeight: 500 }}
            >
              {mode.charAt(0).toUpperCase() + mode.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Screens are now UNLIMITED — the screening-usage meter is intentionally
          NOT rendered on this page (removed with its import). */}

      {/* L4: in-flight ranking indicator (queued/running run for this role). */}
      {inFlight ? (
        <div style={{ marginBottom: "14px" }}>
          <RankingProgressBanner inFlight={inFlight} />
        </div>
      ) : null}

      {/* ── CONTENT — no-jds → LOADING → other empty states → active view ── */}
      {/* Precedence: `no-jds` wins even over loading (see isDataPending note);
          then the pending skeleton (Table / Cards) covers first load + JD-switch
          so a user NEVER sees a false-empty "0 candidates"; only once the query
          has SETTLED do the genuine no-candidates / no-matches states show. */}
      {emptyVariant === "no-jds" ? (
        <CandidatesEmptyState
          variant="no-jds"
          tenantSlug={tenantSlug}
          jdId={jdId}
          onClearFilters={onClearFilters}
        />
      ) : isDataPending ? (
        viewMode === "table" ? (
          <CandidatesTableSkeleton columns={COLUMNS} />
        ) : (
          <CandidatesCardsSkeleton />
        )
      ) : emptyVariant ? (
        <CandidatesEmptyState
          variant={emptyVariant}
          tenantSlug={tenantSlug}
          jdId={jdId}
          onClearFilters={onClearFilters}
        />
      ) : viewMode === "table" ? (
        /* ── TABLE VIEW (default) ── */
        <DataTableV2
          columns={COLUMNS}
          rows={pageRows}
          getRowKey={(row) => row.id}
          renderCell={renderCell}
          onRowClick={onRowClick}
          getRowClassName={getRowClassName}
          sortField={filters.sortKey}
          sortDir={filters.sortDir}
          onSort={(field) => filters.toggleSort(field as SortKey)}
        />
      ) : (
        /* ── CARD VIEW — canonical CandidateCard + inline stage-advance footer. ── */
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(262px, 1fr))",
            gap: "16px",
          }}
        >
          {pageRows.map((row) => (
            <CandidateCard
              key={row.id}
              candidateId={row.id}
              name={row.name}
              status={row.status}
              displayRank={row.rank_position}
              fitScore={row.fit_score}
              fitOverall={row.fit_overall}
              skillsTags={row.skills_tags}
              summary={row.summary}
              tenureSignal={row.tenure_signal}
              selectedJdId={jdId}
              statusControl={
                <CandidateStatusSelect
                  candidateId={row.id}
                  currentStatus={row.status}
                />
              }
            />
          ))}
        </div>
      )}

      {/* ── PAGINATION ───────────────────────────────────────────────────── */}
      {/* 8 per page; shared by both views. Hidden when the filtered list fits
          on a single page. prev/next + page indicator (shadcn outline btns). */}
      {!isDataPending && !emptyVariant && pageCount > 1 && (
        <div
          className="flex items-center justify-between"
          style={{ marginTop: "18px" }}
          data-testid="v2-candidates-pagination"
        >
          <span
            className="text-[hsl(var(--muted-foreground))]"
            style={{ fontSize: "12.5px" }}
          >
            Page {currentPage} of {pageCount}
          </span>
          <div className="flex items-center gap-[8px]">
            <Button
              variant="outline"
              size="sm"
              onClick={() => goToPage(currentPage - 1)}
              disabled={currentPage <= 1}
              aria-label="Previous page"
            >
              <ChevronLeft className="h-3.5 w-3.5" aria-hidden />
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => goToPage(currentPage + 1)}
              disabled={currentPage >= pageCount}
              aria-label="Next page"
            >
              Next
              <ChevronRight className="h-3.5 w-3.5" aria-hidden />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
